import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAdminRole } from '../../../hooks/useAdminRole';
import { SETTINGS_DEFAULTS, refreshGlobalSettings } from '../../../hooks/useSystemSettings';
import type { SystemSettings } from '../../../hooks/useSystemSettings';
import { logAudit } from '../../../lib/audit';

type SettingsMap = SystemSettings;
type Tab = 'branding' | 'seo' | 'tema' | 'contato' | 'financeiro' | 'acesso' | 'seguranca' | 'sistema' | 'pdf';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'branding',   label: 'Branding',        icon: 'ri-palette-line' },
  { id: 'seo',        label: 'SEO & Meta',      icon: 'ri-search-eye-line' },
  { id: 'tema',       label: 'Temas & Cores',   icon: 'ri-brush-line' },
  { id: 'contato',    label: 'Contato & Links', icon: 'ri-links-line' },
  { id: 'financeiro', label: 'Financeiro',      icon: 'ri-money-dollar-circle-line' },
  { id: 'acesso',     label: 'Conta & Acesso',  icon: 'ri-user-settings-line' },
  { id: 'seguranca',  label: 'Segurança',       icon: 'ri-shield-keyhole-line' },
  { id: 'sistema',    label: 'Sistema',         icon: 'ri-server-line' },
  { id: 'pdf',        label: 'Documentos PDF',  icon: 'ri-file-pdf-line' },
];

/* ── Sub-components ── */
const Field = ({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[13px] font-semibold text-white/70">{label}</label>
    {description && <p className="text-[11px] text-white/30 -mt-1">{description}</p>}
    {children}
  </div>
);

const TextInput = ({ value, onChange, placeholder, type = 'text', disabled }: {
  value: string; onChange: (v: string) => void; placeholder?: string; type?: string; disabled?: boolean;
}) => (
  <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} disabled={disabled}
    className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[14px] outline-none focus:border-violet-500 placeholder:text-white/20 disabled:opacity-40 w-full" />
);

const SelectInput = ({ value, onChange, options, disabled }: {
  value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; disabled?: boolean;
}) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
    className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[14px] outline-none focus:border-violet-500 cursor-pointer disabled:opacity-40 w-full">
    {options.map((o) => <option key={o.value} value={o.value} className="bg-[#1A1A2E]">{o.label}</option>)}
  </select>
);

const Toggle = ({ label, description, value, onChange, colorOn = 'bg-violet-600', disabled }: {
  label: string; description?: string; value: string; onChange: (v: string) => void; colorOn?: string; disabled?: boolean;
}) => {
  const isOn = value === 'true';
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <div className="flex-1">
        <p className="text-[13px] font-semibold text-white">{label}</p>
        {description && <p className="text-[11px] text-white/40 mt-0.5">{description}</p>}
      </div>
      <button onClick={() => !disabled && onChange(isOn ? 'false' : 'true')} disabled={disabled}
        className={`w-11 h-6 rounded-full transition-all duration-200 relative cursor-pointer disabled:opacity-40 shrink-0 overflow-hidden ${isOn ? colorOn : 'bg-white/15'}`}>
        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200 ${isOn ? 'left-6' : 'left-1'}`} />
      </button>
    </div>
  );
};

const SectionTitle = ({ title, desc }: { title: string; desc?: string }) => (
  <div className="mb-5">
    <h3 className="text-[16px] font-bold text-white">{title}</h3>
    {desc && <p className="text-[12px] text-white/40 mt-0.5">{desc}</p>}
  </div>
);

const ColorField = ({ label, description, colorKey, settings, set, disabled }: {
  label: string; description?: string; colorKey: keyof SettingsMap; settings: SettingsMap;
  set: (k: keyof SettingsMap) => (v: string) => void; disabled?: boolean;
}) => (
  <Field label={label} description={description}>
    <div className="flex items-center gap-3">
      <input type="color" value={String(settings[colorKey]) || '#7c3aed'}
        onChange={(e) => set(colorKey)(e.target.value)} disabled={disabled}
        className="w-12 h-10 rounded-xl border border-white/10 bg-transparent cursor-pointer shrink-0" />
      <TextInput value={String(settings[colorKey])} onChange={set(colorKey)} placeholder="#7c3aed" disabled={disabled} />
    </div>
  </Field>
);

/* ── Image Upload ── */
interface ImageUploadProps {
  label: string; description?: string; currentUrl: string;
  onUploaded: (url: string) => void; storagePath: string;
  disabled?: boolean; previewClass?: string;
}
const ImageUpload = ({ label, description, currentUrl, onUploaded, storagePath, disabled, previewClass = 'h-12' }: ImageUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setErr('Imagem muito grande. Máximo 2MB.'); return; }
    setErr(''); setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${storagePath}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('platform-assets').upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from('platform-assets').getPublicUrl(path);
      onUploaded(data.publicUrl);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Erro no upload');
    } finally { setUploading(false); }
  };

  return (
    <Field label={label} description={description}>
      <div className="flex items-center gap-3">
        {currentUrl ? (
          <div className="relative group">
            <img src={currentUrl} alt={label} className={`${previewClass} object-contain rounded-lg border border-white/10`} />
            {!disabled && (
              <button onClick={() => onUploaded('')}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <i className="ri-close-line text-white text-[10px]" />
              </button>
            )}
          </div>
        ) : (
          <div className="w-12 h-12 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center text-white/30 shrink-0">
            <i className="ri-image-line text-xl" />
          </div>
        )}
        <div className="flex-1 flex flex-col gap-1.5">
          <button onClick={() => inputRef.current?.click()} disabled={disabled || uploading}
            className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/15 text-white text-[13px] rounded-lg transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap w-fit">
            {uploading ? <><i className="ri-loader-4-line animate-spin" />Enviando...</> : <><i className="ri-upload-cloud-line" />Enviar imagem</>}
          </button>
          <p className="text-[11px] text-white/30">JPG, PNG, WebP — máx. 2MB</p>
          {err && <p className="text-[11px] text-red-400">{err}</p>}
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
    </Field>
  );
};

/* ── Main Page ── */
const AdminConfiguracoesPage = () => {
  const { isMasterAdmin } = useAdminRole();
  const [settings, setSettings] = useState<SettingsMap>(SETTINGS_DEFAULTS);
  const [activeTab, setActiveTab] = useState<Tab>('branding');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const set = useCallback((key: keyof SettingsMap) => (val: string) =>
    setSettings((prev) => ({ ...prev, [key]: val })), []);

  const loadSettings = useCallback(async () => {
    const { data } = await supabase.from('system_settings').select('key, value');
    if (!data) return;
    const map: Record<string, string> = {};
    data.forEach((row: { key: string; value: string }) => { map[row.key] = row.value ?? ''; });
    setSettings((prev) => ({ ...prev, ...map }));
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async () => {
    if (!isMasterAdmin) return;
    setSaving(true); setError('');
    try {
      const upsertData = Object.entries(settings).map(([key, value]) => ({
        key, value, updated_at: new Date().toISOString(),
      }));
      const { error: err } = await supabase.from('system_settings').upsert(upsertData, { onConflict: 'key' });
      if (err) throw err;
      await refreshGlobalSettings();
      await logAudit({ action: 'settings_change', resource: 'settings', details: { tab: activeTab } });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar configurações.');
    } finally { setSaving(false); }
  };

  const readOnly = !isMasterAdmin;

  /* ════════════════════════════════════════════════
     TAB: BRANDING
  ════════════════════════════════════════════════ */
  const renderBranding = () => (
    <div className="space-y-8">
      <SectionTitle title="Identidade Visual" desc="Nome, logo e favicon exibidos em toda a plataforma" />
      <div className="grid grid-cols-1 gap-6">
        <Field label="Nome da Plataforma" description="Exibido em todo o sistema, e-mails e páginas públicas">
          <TextInput value={settings.platform_name} onChange={set('platform_name')} placeholder="Ex: FinançasPro" disabled={readOnly} />
        </Field>
        <Field label="Slogan / Subtítulo" description="Tagline exibida no login e onboarding">
          <TextInput value={settings.platform_slogan} onChange={set('platform_slogan')} placeholder="Ex: Controle financeiro inteligente" disabled={readOnly} />
        </Field>
      </div>

      <div className="border-t border-white/10 pt-6 grid grid-cols-2 gap-8">
        <ImageUpload label="Logo Principal" description="Recomendado: PNG transparente, 200×60px"
          currentUrl={settings.logo_url} onUploaded={set('logo_url')} storagePath="logo-main"
          disabled={readOnly} previewClass="h-10 max-w-[160px]" />
        <ImageUpload label="Logo Reduzida" description="Recomendado: ícone quadrado 64×64px"
          currentUrl={settings.logo_small_url} onUploaded={set('logo_small_url')} storagePath="logo-small"
          disabled={readOnly} previewClass="h-10 w-10" />
      </div>

      {/* Landing page texts */}
      <div className="border-t border-white/10 pt-6 space-y-6">
        <SectionTitle title="Textos da Página de Login" desc="Edite o título, destaques, features e estatísticas exibidos na tela de login" />

        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">
          <p className="text-[12px] text-white/40 font-semibold uppercase tracking-wide">Hero — Título principal</p>
          <div className="grid grid-cols-2 gap-5">
            <Field label="Linha do título" description="Primeira linha do cabeçalho">
              <TextInput value={settings.landing_hero_title} onChange={set('landing_hero_title')} placeholder="Controle total das suas" disabled={readOnly} />
            </Field>
            <Field label="Palavra destaque (em verde)" description="A parte colorida/destacada do título">
              <TextInput value={settings.landing_hero_highlight} onChange={set('landing_hero_highlight')} placeholder="finanças pessoais" disabled={readOnly} />
            </Field>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3">
            <p className="text-[11px] text-white/30 mb-2 uppercase tracking-wide">Pré-visualização:</p>
            <p className="text-[18px] font-800 text-white leading-snug">
              {settings.landing_hero_title || 'Controle total das suas'}<br />
              suas <span style={{ color: settings.app_primary_color || '#10b981' }}>
                {settings.landing_hero_highlight || 'finanças pessoais'}
              </span>
            </p>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          <p className="text-[12px] text-white/40 font-semibold uppercase tracking-wide">Features — Lista de benefícios</p>
          <div className="grid grid-cols-1 gap-4">
            {([
              { key: 'landing_feature_1' as const, placeholder: 'Visão completa das suas finanças' },
              { key: 'landing_feature_2' as const, placeholder: 'Dados isolados e seguros por usuário' },
              { key: 'landing_feature_3' as const, placeholder: 'Metas financeiras inteligentes' },
              { key: 'landing_feature_4' as const, placeholder: 'Controle de cartões e faturas' },
            ]).map((f, i) => (
              <Field key={f.key} label={`Feature ${i + 1}`}>
                <TextInput value={settings[f.key]} onChange={set(f.key)} placeholder={f.placeholder} disabled={readOnly} />
              </Field>
            ))}
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          <p className="text-[12px] text-white/40 font-semibold uppercase tracking-wide">Estatísticas sociais (ex: +12mil usuários)</p>
          <div className="grid grid-cols-3 gap-5">
            {([
              { valKey: 'landing_stat_1_val' as const, labelKey: 'landing_stat_1_label' as const, valDefault: '+12mil', labelDefault: 'usuários ativos' },
              { valKey: 'landing_stat_2_val' as const, labelKey: 'landing_stat_2_label' as const, valDefault: '4.9★', labelDefault: 'avaliação média' },
              { valKey: 'landing_stat_3_val' as const, labelKey: 'landing_stat_3_label' as const, valDefault: '100%', labelDefault: 'dados seguros' },
            ]).map((s, i) => (
              <div key={i} className="space-y-3">
                <Field label={`Stat ${i + 1} — Valor`}>
                  <TextInput value={settings[s.valKey]} onChange={set(s.valKey)} placeholder={s.valDefault} disabled={readOnly} />
                </Field>
                <Field label="Legenda">
                  <TextInput value={settings[s.labelKey]} onChange={set(s.labelKey)} placeholder={s.labelDefault} disabled={readOnly} />
                </Field>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="border-t border-white/10 pt-6">
        <SectionTitle title="Pré-visualização do Branding" desc="Como o branding aparece no topo do sistema" />
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center gap-4">
          {settings.logo_url ? (
            <img src={settings.logo_url} alt="Logo" className="h-9 object-contain" />
          ) : (
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: settings.admin_primary_color || '#7c3aed' }}>
              <i className="ri-shield-star-line text-white text-base" />
            </div>
          )}
          <div>
            <p className="text-[15px] font-bold text-white">{settings.platform_name || 'Nome da Plataforma'}</p>
            <p className="text-[11px]" style={{ color: settings.admin_secondary_color || '#06b6d4' }}>
              {settings.platform_slogan || 'Subtítulo da plataforma'}
            </p>
          </div>
          <div className="ml-auto flex gap-2 flex-wrap">
            <span className="px-3 py-1.5 rounded-full text-[12px] font-semibold text-white"
              style={{ backgroundColor: settings.admin_primary_color || '#7c3aed' }}>Botão Admin</span>
            <span className="px-3 py-1.5 rounded-full text-[12px] font-semibold"
              style={{ color: settings.app_primary_color || '#10b981', borderColor: settings.app_primary_color || '#10b981', border: '1px solid' }}>Botão App</span>
          </div>
        </div>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════
     TAB: SEO & META
  ════════════════════════════════════════════════ */
  const renderSeo = () => {
    const previewTitle = settings.seo_title || settings.platform_name || 'Nome da Plataforma';
    const previewDesc  = settings.seo_description || settings.platform_slogan || 'Descrição da plataforma exibida nos buscadores.';
    const previewUrl   = settings.site_url || settings.main_domain || 'meuapp.com.br';
    const previewImg   = settings.seo_og_image || settings.logo_url || '';

    return (
      <div className="space-y-8">

        {/* Favicon */}
        <div>
          <SectionTitle title="Favicon & Ícone do Navegador" desc="Ícone exibido na aba do navegador e nos bookmarks" />
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">
            <ImageUpload
              label="Favicon" description="Recomendado: PNG quadrado 32×32 ou 64×64px. Se vazio, usa a Logo Reduzida do Branding."
              currentUrl={settings.favicon_url}
              onUploaded={set('favicon_url')}
              storagePath="favicon"
              disabled={readOnly}
              previewClass="h-10 w-10"
            />
            {/* Browser tab simulation */}
            <div>
              <p className="text-[11px] text-white/30 mb-2 uppercase tracking-wide">Simulação de aba do navegador:</p>
              <div className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-xl w-fit">
                {(settings.favicon_url || settings.logo_small_url) ? (
                  <img src={settings.favicon_url || settings.logo_small_url} alt="tab icon" className="w-4 h-4 object-contain rounded shrink-0" />
                ) : (
                  <div className="w-4 h-4 rounded bg-emerald-500 shrink-0" />
                )}
                <span className="text-[12px] text-white/60 truncate max-w-[220px]">{previewTitle}</span>
                <i className="ri-close-line text-white/30 text-[12px] ml-1 shrink-0" />
              </div>
            </div>
          </div>
        </div>

        {/* SEO básico */}
        <div className="border-t border-white/10 pt-6">
          <SectionTitle title="SEO Básico" desc="Informações que aparecem nas buscas do Google, Bing e outros buscadores" />
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">

            <Field label="Título da página (title tag)" description="Aparece na aba do navegador e no Google. Recomendado: até 60 caracteres.">
              <div className="space-y-1">
                <TextInput value={settings.seo_title} onChange={set('seo_title')}
                  placeholder={`${settings.platform_name || 'Plataforma'} — Controle Financeiro Pessoal`} disabled={readOnly} />
                <div className="flex justify-between">
                  <p className="text-[11px] text-white/30">{(settings.seo_title || '').length} / 60 caracteres</p>
                  {(settings.seo_title || '').length > 60 && (
                    <p className="text-[11px] text-amber-400">Título muito longo — Google pode cortar</p>
                  )}
                </div>
              </div>
            </Field>

            <Field label="Meta description" description="Resumo exibido sob o título nos resultados de busca. Recomendado: 120–160 caracteres.">
              <div className="space-y-1">
                <textarea
                  value={settings.seo_description}
                  onChange={(e) => set('seo_description')(e.target.value)}
                  disabled={readOnly}
                  rows={3}
                  maxLength={200}
                  placeholder="Organize suas finanças pessoais, controle gastos, defina metas e acompanhe cartões em um só lugar."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[14px] outline-none focus:border-violet-500 resize-none placeholder:text-white/20 disabled:opacity-40"
                />
                <div className="flex justify-between">
                  <p className="text-[11px] text-white/30">{(settings.seo_description || '').length} / 160 caracteres</p>
                  {(settings.seo_description || '').length > 160 && (
                    <p className="text-[11px] text-amber-400">Descrição muito longa — Google pode cortar</p>
                  )}
                </div>
              </div>
            </Field>

            <div className="grid grid-cols-2 gap-5">
              <Field label="Keywords (palavras-chave)" description="Separadas por vírgula">
                <TextInput value={settings.seo_keywords} onChange={set('seo_keywords')}
                  placeholder="finanças pessoais, controle financeiro, metas" disabled={readOnly} />
              </Field>
              <Field label="Author (autor / empresa)" description="Nome exibido na meta author">
                <TextInput value={settings.seo_author} onChange={set('seo_author')}
                  placeholder={settings.platform_name || 'Nome da Empresa'} disabled={readOnly} />
              </Field>
            </div>

            {/* Google snippet preview */}
            <div className="border-t border-white/10 pt-5">
              <p className="text-[11px] text-white/40 font-semibold uppercase tracking-wide mb-3">Prévia — Resultado do Google</p>
              <div className="bg-white rounded-xl p-4">
                <p className="text-[12px] text-gray-500 mb-0.5">{previewUrl}</p>
                <p className="text-[18px] font-medium leading-snug mb-1" style={{ color: '#1a0dab' }}>{previewTitle}</p>
                <p className="text-[13px] text-gray-700 leading-relaxed">{previewDesc}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Open Graph */}
        <div className="border-t border-white/10 pt-6">
          <SectionTitle title="Open Graph — Compartilhamento Social" desc="Como o link aparece ao ser compartilhado no WhatsApp, LinkedIn, Facebook, X etc." />
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">
            <ImageUpload
              label="Imagem de compartilhamento (OG Image)" description="Recomendado: 1200×630px, JPG ou PNG. Se vazio, usa a Logo Principal."
              currentUrl={settings.seo_og_image}
              onUploaded={set('seo_og_image')}
              storagePath="og-image"
              disabled={readOnly}
              previewClass="h-14 max-w-[280px]"
            />

            {/* Social card preview */}
            <div className="border-t border-white/10 pt-4">
              <p className="text-[11px] text-white/40 font-semibold uppercase tracking-wide mb-3">Prévia — Card de Compartilhamento</p>
              <div className="bg-white rounded-xl overflow-hidden max-w-[360px] border border-gray-200">
                {previewImg ? (
                  <img src={previewImg} alt="OG preview" className="w-full h-40 object-cover object-top" />
                ) : (
                  <div className="w-full h-40 bg-gray-100 flex flex-col items-center justify-center gap-2">
                    <i className="ri-image-add-line text-3xl text-gray-300" />
                    <p className="text-[11px] text-gray-400">Adicione uma OG Image</p>
                  </div>
                )}
                <div className="px-3 py-3 space-y-0.5">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">{previewUrl.replace(/https?:\/\//, '')}</p>
                  <p className="text-[14px] font-semibold text-gray-900 leading-snug">{previewTitle}</p>
                  <p className="text-[12px] text-gray-500 line-clamp-2">{previewDesc}</p>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="flex items-start gap-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3">
              <i className="ri-information-line text-emerald-400 text-base shrink-0 mt-0.5" />
              <div className="text-[12px] text-emerald-300 space-y-1">
                <p>Título e descrição do OG usam os campos SEO Básico acima.</p>
                <p><strong>og:url</strong> usa a URL do Site configurada em Contato &amp; Links.</p>
                <p>Tags <strong>Twitter/X Cards</strong> são geradas automaticamente com os mesmos dados ao salvar.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ════════════════════════════════════════════════
     TAB: TEMAS & CORES
  ════════════════════════════════════════════════ */
  const renderTema = () => (
    <div className="space-y-8">
      <SectionTitle title="Tema do Painel Master" desc="Cores aplicadas no painel administrativo" />
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="grid grid-cols-2 gap-6">
          <ColorField label="Cor Principal — Admin" description="Botões, sidebar, destaques do painel master"
            colorKey="admin_primary_color" settings={settings} set={set} disabled={readOnly} />
          <ColorField label="Cor Secundária — Admin" description="Badges, status, links do painel master"
            colorKey="admin_secondary_color" settings={settings} set={set} disabled={readOnly} />
        </div>
        <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-3">
          <span className="text-[12px] text-white/40">Pré-visualização Admin:</span>
          <span className="px-3 py-1 rounded-lg text-[12px] font-semibold text-white"
            style={{ backgroundColor: settings.admin_primary_color || '#7c3aed' }}>Primária</span>
          <span className="px-3 py-1 rounded-lg text-[12px] font-semibold text-white"
            style={{ backgroundColor: settings.admin_secondary_color || '#06b6d4' }}>Secundária</span>
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <SectionTitle title="Tema do App do Usuário" desc="Cores aplicadas na área do cliente final" />
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <div className="grid grid-cols-2 gap-6">
            <ColorField label="Cor Principal — App" description="Botões, destaques na área do cliente"
              colorKey="app_primary_color" settings={settings} set={set} disabled={readOnly} />
            <ColorField label="Cor Secundária — App" description="Badges, status na área do cliente"
              colorKey="app_secondary_color" settings={settings} set={set} disabled={readOnly} />
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-3">
            <span className="text-[12px] text-white/40">Pré-visualização App:</span>
            <span className="px-3 py-1 rounded-lg text-[12px] font-semibold text-white"
              style={{ backgroundColor: settings.app_primary_color || '#10b981' }}>Primária</span>
            <span className="px-3 py-1 rounded-lg text-[12px] font-semibold text-white"
              style={{ backgroundColor: settings.app_secondary_color || '#06b6d4' }}>Secundária</span>
          </div>
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 flex items-start gap-3">
        <i className="ri-information-line text-amber-400 text-lg shrink-0 mt-0.5" />
        <p className="text-[12px] text-amber-300">As variáveis CSS <code className="text-amber-200">--admin-primary</code>, <code className="text-amber-200">--app-primary</code> etc. são aplicadas automaticamente ao salvar, refletindo em todo o sistema sem recarregar.</p>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════
     TAB: CONTATO & LINKS
  ════════════════════════════════════════════════ */
  const renderContato = () => (
    <div className="space-y-8">
      <SectionTitle title="E-mails e Contato" desc="Dados de contato exibidos ao usuário e usados pelo sistema" />
      <div className="grid grid-cols-2 gap-6">
        <Field label="E-mail de Suporte" description="Exibido na central de ajuda ao cliente">
          <TextInput value={settings.support_email} onChange={set('support_email')} placeholder="suporte@empresa.com" type="email" disabled={readOnly} />
        </Field>
        <Field label="E-mail Financeiro" description="Recebimento de notificações de pagamento">
          <TextInput value={settings.finance_email} onChange={set('finance_email')} placeholder="financeiro@empresa.com" type="email" disabled={readOnly} />
        </Field>
        <Field label="Telefone / WhatsApp">
          <TextInput value={settings.whatsapp} onChange={set('whatsapp')} placeholder="+55 11 9 9999-9999" disabled={readOnly} />
        </Field>
        <Field label="URL da Central de Ajuda" description="Link para documentação ou FAQ exibido ao cliente">
          <TextInput value={settings.help_url} onChange={set('help_url')} placeholder="https://ajuda.meuapp.com.br" disabled={readOnly} />
        </Field>
      </div>
      <div className="border-t border-white/10 pt-6">
        <SectionTitle title="URLs e Domínio" />
        <div className="grid grid-cols-2 gap-6">
          <Field label="Domínio Principal">
            <TextInput value={settings.main_domain} onChange={set('main_domain')} placeholder="meuapp.com.br" disabled={readOnly} />
          </Field>
          <Field label="URL do Site">
            <TextInput value={settings.site_url} onChange={set('site_url')} placeholder="https://meuapp.com.br" disabled={readOnly} />
          </Field>
          <Field label="URL da Política de Privacidade">
            <TextInput value={settings.privacy_policy_url} onChange={set('privacy_policy_url')} placeholder="https://meuapp.com.br/privacidade" disabled={readOnly} />
          </Field>
          <Field label="URL dos Termos de Uso">
            <TextInput value={settings.terms_url} onChange={set('terms_url')} placeholder="https://meuapp.com.br/termos" disabled={readOnly} />
          </Field>
        </div>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════
     TAB: FINANCEIRO
  ════════════════════════════════════════════════ */
  const renderFinanceiro = () => (
    <div className="space-y-8">
      <SectionTitle title="Configurações Monetárias" />
      <div className="grid grid-cols-2 gap-6">
        <Field label="Moeda Padrão Global">
          <SelectInput value={settings.default_currency} onChange={set('default_currency')} disabled={readOnly}
            options={[
              { value: 'BRL', label: 'BRL — Real Brasileiro' },
              { value: 'USD', label: 'USD — Dólar Americano' },
              { value: 'EUR', label: 'EUR — Euro' },
              { value: 'GBP', label: 'GBP — Libra Esterlina' },
            ]} />
        </Field>
        <Field label="Formato Monetário">
          <SelectInput value={settings.currency_format} onChange={set('currency_format')} disabled={readOnly}
            options={[
              { value: 'pt-BR', label: 'pt-BR (R$ 1.234,56)' },
              { value: 'en-US', label: 'en-US ($1,234.56)' },
              { value: 'de-DE', label: 'de-DE (1.234,56 €)' },
            ]} />
        </Field>
        <Field label="Fuso Horário Padrão">
          <SelectInput value={settings.timezone} onChange={set('timezone')} disabled={readOnly}
            options={[
              { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT -3)' },
              { value: 'America/Manaus', label: 'America/Manaus (AMT -4)' },
              { value: 'America/New_York', label: 'America/New_York (EST -5)' },
              { value: 'Europe/London', label: 'Europe/London (GMT 0)' },
              { value: 'UTC', label: 'UTC (0)' },
            ]} />
        </Field>
        <Field label="Trial Padrão (dias)">
          <TextInput value={settings.default_trial_days} onChange={set('default_trial_days')} placeholder="7" type="number" disabled={readOnly} />
        </Field>
        <Field label="Gateway Padrão">
          <SelectInput value={settings.default_gateway} onChange={set('default_gateway')} disabled={readOnly}
            options={[
              { value: 'stripe', label: 'Stripe' },
              { value: 'mercado_pago', label: 'Mercado Pago' },
              { value: 'asaas', label: 'Asaas' },
              { value: 'pagarme', label: 'Pagar.me' },
            ]} />
        </Field>
        <Field label="Ambiente Global">
          <SelectInput value={settings.global_environment} onChange={set('global_environment')} disabled={readOnly}
            options={[
              { value: 'sandbox', label: 'Sandbox (Testes)' },
              { value: 'production', label: 'Produção (Real)' },
            ]} />
        </Field>
      </div>
      {settings.global_environment === 'production' && (
        <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
          <i className="ri-alert-line text-amber-400 text-lg shrink-0" />
          <p className="text-[13px] text-amber-300">Ambiente de <strong>Produção</strong> ativo. Cobranças reais serão realizadas.</p>
        </div>
      )}
    </div>
  );

  /* ════════════════════════════════════════════════
     TAB: CONTA & ACESSO
  ════════════════════════════════════════════════ */
  const renderAcesso = () => (
    <div className="space-y-8">
      <SectionTitle title="Cadastro de Usuários" />
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <Toggle label="Permitir cadastro público" description="Qualquer pessoa pode criar uma conta"
          value={settings.allow_public_signup} onChange={set('allow_public_signup')} disabled={readOnly} />
        <div className="border-t border-white/10" />
        <Toggle label="Exigir confirmação de e-mail" description="Usuário deve confirmar antes de acessar"
          value={settings.require_email_confirm} onChange={set('require_email_confirm')} disabled={readOnly} />
        <div className="border-t border-white/10" />
        <Toggle label="Aprovação manual de cadastro" description="Novos cadastros precisam de aprovação admin"
          value={settings.manual_approval} onChange={set('manual_approval')} disabled={readOnly} />
      </div>
      <div className="border-t border-white/10 pt-6">
        <SectionTitle title="Ações de Conta" />
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          <Toggle label="Permitir exclusão de conta" value={settings.allow_account_delete} onChange={set('allow_account_delete')} disabled={readOnly} />
          <div className="border-t border-white/10" />
          <Toggle label="Permitir downgrade de plano" value={settings.allow_plan_downgrade} onChange={set('allow_plan_downgrade')} disabled={readOnly} />
        </div>
      </div>

      {/* Welcome notification */}
      <div className="border-t border-white/10 pt-6">
        <SectionTitle title="Notificação de Boas-vindas" desc="Mensagem enviada automaticamente quando um novo usuário conclui o cadastro" />
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">
          <Toggle label="Enviar notificação de boas-vindas" description="Disparar automaticamente ao final do wizard de cadastro"
            value={settings.welcome_notification_enabled} onChange={set('welcome_notification_enabled')}
            colorOn="bg-emerald-600" disabled={readOnly} />
          {settings.welcome_notification_enabled !== 'false' && (
            <>
              <div className="border-t border-white/10" />
              <Field label="Título da notificação" description="Use {nome} para inserir o primeiro nome do usuário">
                <TextInput value={settings.welcome_notification_title} onChange={set('welcome_notification_title')}
                  placeholder="Bem-vindo(a), {nome}! 🎉" disabled={readOnly} />
              </Field>
              <Field label="Corpo da mensagem" description="Use {nome} para personalizar.">
                <textarea value={settings.welcome_notification_body}
                  onChange={(e) => set('welcome_notification_body')(e.target.value)}
                  disabled={readOnly} rows={3} maxLength={300}
                  placeholder="Sua conta foi criada com sucesso. Explore o dashboard..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[14px] outline-none focus:border-violet-500 resize-none placeholder:text-white/20 disabled:opacity-40"
                />
                <p className="text-[11px] text-white/30 mt-0.5">{(settings.welcome_notification_body || '').length}/300 caracteres</p>
              </Field>
              <div className="border-t border-white/10 pt-4">
                <p className="text-[11px] text-white/40 mb-3 font-semibold uppercase tracking-wide">Pré-visualização</p>
                <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                    <i className="ri-hand-heart-line text-emerald-400 text-[16px]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-white truncate">
                      {(settings.welcome_notification_title || 'Bem-vindo(a), {nome}! 🎉').replace(/\{nome\}/gi, 'João')}
                    </p>
                    <p className="text-[12px] text-white/50 mt-0.5 line-clamp-2">
                      {(settings.welcome_notification_body || 'Sua conta foi criada com sucesso!').replace(/\{nome\}/gi, 'João')}
                    </p>
                  </div>
                  <span className="text-[10px] text-white/30 shrink-0 mt-0.5">agora</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════
     TAB: SEGURANÇA
  ════════════════════════════════════════════════ */
  const renderSeguranca = () => (
    <div className="space-y-8">
      <SectionTitle title="Autenticação e Proteção" />
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <Toggle label="2FA obrigatório para administradores" description="Todos os admins devem usar autenticação de dois fatores"
          value={settings.admin_2fa} onChange={set('admin_2fa')} colorOn="bg-emerald-600" disabled={readOnly} />
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label="Expiração de sessão (horas)">
          <TextInput value={settings.session_expiry_hours} onChange={set('session_expiry_hours')} placeholder="24" type="number" disabled={readOnly} />
        </Field>
        <Field label="Máximo de tentativas de login">
          <TextInput value={settings.max_login_attempts} onChange={set('max_login_attempts')} placeholder="5" type="number" disabled={readOnly} />
        </Field>
      </div>
      <div className="border-t border-white/10 pt-6">
        <SectionTitle title="Papéis Administrativos" />
        <div className="space-y-3">
          {[
            { role: 'master_admin', label: 'Master Admin', desc: 'Acesso total', color: 'bg-violet-500/20 text-violet-300', border: 'border-violet-500/30' },
            { role: 'admin',        label: 'Admin',        desc: 'Dashboard, usuários, planos', color: 'bg-slate-500/20 text-slate-300', border: 'border-slate-500/30' },
            { role: 'support',      label: 'Suporte',      desc: 'Visualizar clientes e assinaturas', color: 'bg-cyan-500/20 text-cyan-300', border: 'border-cyan-500/30' },
            { role: 'finance',      label: 'Financeiro',   desc: 'Pagamentos e gateways', color: 'bg-emerald-500/20 text-emerald-300', border: 'border-emerald-500/30' },
            { role: 'customer',     label: 'Cliente',      desc: 'Área do cliente final', color: 'bg-white/10 text-white/50', border: 'border-white/10' },
          ].map((r) => (
            <div key={r.role} className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${r.border} ${r.color.split(' ')[0]}`}>
              <i className="ri-shield-check-line text-lg opacity-70" />
              <div className="flex-1">
                <p className={`text-[13px] font-bold ${r.color.split(' ')[1]}`}>{r.label}</p>
                <p className="text-[11px] text-white/40">{r.desc}</p>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.color}`}>{r.role}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════
     TAB: SISTEMA
  ════════════════════════════════════════════════ */
  const renderSistema = () => (
    <div className="space-y-8">
      <SectionTitle title="Modo de Manutenção" />
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <Toggle label="Ativar modo manutenção" description="Bloqueia acesso de clientes — apenas admins passam"
          value={settings.maintenance_mode} onChange={set('maintenance_mode')} colorOn="bg-red-600" disabled={readOnly} />
        {settings.maintenance_mode === 'true' && (
          <>
            <div className="border-t border-white/10" />
            <Field label="Mensagem de manutenção">
              <textarea value={settings.maintenance_message}
                onChange={(e) => set('maintenance_message')(e.target.value)}
                disabled={readOnly} rows={3} maxLength={500}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[14px] outline-none focus:border-violet-500 resize-none" />
            </Field>
          </>
        )}
      </div>
      {settings.maintenance_mode === 'true' && (
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
          <i className="ri-error-warning-line text-red-400 text-lg shrink-0" />
          <p className="text-[13px] text-red-300"><strong>Modo de manutenção ativo!</strong> Clientes não conseguem acessar o sistema agora.</p>
        </div>
      )}
      <div className="border-t border-white/10 pt-6">
        <SectionTitle title="Diagnóstico" />
        <div className="grid grid-cols-2 gap-6">
          <Field label="Versão do Sistema">
            <TextInput value={settings.system_version} onChange={set('system_version')} placeholder="1.0.0" disabled={readOnly} />
          </Field>
        </div>
        <div className="mt-4 bg-white/5 border border-white/10 rounded-2xl p-5">
          <Toggle label="Logs habilitados" description="Registrar ações em audit_logs"
            value={settings.logs_enabled} onChange={set('logs_enabled')} colorOn="bg-emerald-600" disabled={readOnly} />
        </div>
      </div>
      <div className="border-t border-white/10 pt-6">
        <SectionTitle title="Tabelas do Sistema" />
        <div className="grid grid-cols-3 gap-4">
          {['user_roles', 'subscriptions', 'payments', 'payment_gateways', 'system_settings', 'audit_logs'].map((t) => (
            <div key={t} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 flex items-center gap-3">
              <i className="ri-database-2-line text-violet-400 text-lg" />
              <span className="text-[12px] text-white/70 font-medium truncate">{t}</span>
              <span className="ml-auto text-[10px] text-emerald-400 font-semibold bg-emerald-500/10 px-1.5 py-0.5 rounded-full shrink-0">OK</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'branding':   return renderBranding();
      case 'seo':        return renderSeo();
      case 'tema':       return renderTema();
      case 'contato':    return renderContato();
      case 'financeiro': return renderFinanceiro();
      case 'acesso':     return renderAcesso();
      case 'seguranca':  return renderSeguranca();
      case 'sistema':    return renderSistema();
      case 'pdf':        return renderPdf();
      default:           return null;
    }
  };

  /* ════════════════════════════════════════════════
     TAB: DOCUMENTOS PDF
  ════════════════════════════════════════════════ */
  const renderPdf = () => (
    <div className="space-y-8">
      <SectionTitle title="Identidade do Documento" desc="Informações que aparecem no cabeçalho e rodapé de todos os PDFs gerados" />

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">
        <div className="grid grid-cols-2 gap-6">
          <Field label="Nome da empresa no PDF" description="Exibido no cabeçalho de todos os documentos. Se vazio, usa o nome da plataforma.">
            <TextInput value={settings.pdf_company_name} onChange={set('pdf_company_name')} placeholder={settings.platform_name || 'Ex: PandaPro'} disabled={readOnly} />
          </Field>
          <Field label="Subtítulo do documento" description="Segunda linha do cabeçalho">
            <TextInput value={settings.pdf_subtitle} onChange={set('pdf_subtitle')} placeholder="Relatório Financeiro" disabled={readOnly} />
          </Field>
        </div>

        <Field label="Texto de rodapé" description="Exibido no rodapé esquerdo de cada página">
          <TextInput value={settings.pdf_footer_text} onChange={set('pdf_footer_text')} placeholder="Documento gerado automaticamente pelo sistema" disabled={readOnly} />
        </Field>

        <Field label="Nota legal / aviso" description="Texto de aviso legal opcional, exibido abaixo do rodapé">
          <TextInput value={settings.pdf_legal_note} onChange={set('pdf_legal_note')} placeholder="Ex: Documento de uso interno. Não deve ser distribuído externamente." disabled={readOnly} />
        </Field>

        <Field label="Prefixo do nome do arquivo" description="Nome do arquivo baixado será: {prefixo}-{aba}-{data}.pdf">
          <TextInput value={settings.pdf_filename_prefix} onChange={set('pdf_filename_prefix')} placeholder="relatorio" disabled={readOnly} />
        </Field>
      </div>

      <div className="border-t border-white/10 pt-6">
        <SectionTitle title="Visual do Documento" desc="Cor e logo usados na identidade visual dos PDFs" />
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">
          <ColorField
            label="Cor principal do PDF"
            description="Usada em cabeçalhos de tabelas, barras de destaque e elementos visuais"
            colorKey="pdf_primary_color"
            settings={settings}
            set={set}
            disabled={readOnly}
          />

          <div className="border-t border-white/10 pt-4">
            <ImageUpload
              label="Logo para documentos PDF"
              description="Se vazio, usa a Logo Principal do Branding. Recomendado: PNG transparente, 200×60px"
              currentUrl={settings.pdf_logo_url}
              onUploaded={set('pdf_logo_url')}
              storagePath="pdf-logo"
              disabled={readOnly}
              previewClass="h-10 max-w-[160px]"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <SectionTitle title="Seções do Documento" desc="Controle quais blocos aparecem nos PDFs exportados pelos usuários" />
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          <Toggle
            label="Mostrar logo no cabeçalho"
            description="Exibe a logo da empresa no canto esquerdo de cada página"
            value={settings.pdf_show_logo}
            onChange={set('pdf_show_logo')}
            colorOn="bg-emerald-600"
            disabled={readOnly}
          />
          <div className="border-t border-white/10" />
          <Toggle
            label="Incluir resumo executivo"
            description="Bloco com os 4 cards principais: Receitas, Despesas, Saldo, Pendente"
            value={settings.pdf_show_summary}
            onChange={set('pdf_show_summary')}
            colorOn="bg-emerald-600"
            disabled={readOnly}
          />
          <div className="border-t border-white/10" />
          <Toggle
            label="Incluir análise por categorias"
            description="Tabelas de gastos e receitas por categoria"
            value={settings.pdf_show_categories}
            onChange={set('pdf_show_categories')}
            colorOn="bg-emerald-600"
            disabled={readOnly}
          />
          <div className="border-t border-white/10" />
          <Toggle
            label="Incluir tabela de lançamentos"
            description="Lista detalhada de todos os lançamentos filtrados (até 500 registros)"
            value={settings.pdf_show_transactions}
            onChange={set('pdf_show_transactions')}
            colorOn="bg-emerald-600"
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <SectionTitle title="Pré-visualização da Estrutura" desc="Como os documentos gerados são organizados" />
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <div className="flex flex-col gap-2">
            {[
              { icon: 'ri-layout-top-line', label: 'Cabeçalho', desc: 'Logo + nome + subtítulo + linha de cor primária' },
              { icon: 'ri-bar-chart-grouped-line', label: 'Resumo Executivo', desc: '4 cards de métricas + 6 indicadores secundários', toggle: settings.pdf_show_summary },
              { icon: 'ri-calendar-line', label: 'Comparativo Mensal', desc: 'Tabela com receitas, despesas e saldo por mês', toggle: 'true' },
              { icon: 'ri-pie-chart-2-line', label: 'Análise por Categorias', desc: 'Tabelas de despesas e receitas por categoria', toggle: settings.pdf_show_categories },
              { icon: 'ri-table-line', label: 'Lançamentos', desc: 'Tabela analítica detalhada com todos os dados', toggle: settings.pdf_show_transactions },
              { icon: 'ri-layout-bottom-line', label: 'Rodapé', desc: 'Texto de rodapé + nota legal + numeração de páginas' },
            ].map((section, i) => {
              const active = section.toggle == null || section.toggle === 'true';
              return (
                <div key={i} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${active ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-white/3 border-white/5 opacity-40'}`}>
                  <div className="w-7 h-7 flex items-center justify-center shrink-0">
                    <i className={`${section.icon} text-[16px] ${active ? 'text-emerald-400' : 'text-white/30'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-[12px] font-semibold ${active ? 'text-white' : 'text-white/40'}`}>{section.label}</p>
                    <p className="text-[11px] text-white/30 truncate">{section.desc}</p>
                  </div>
                  {section.toggle != null && (
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-white/30'}`}>
                      {active ? 'Ativo' : 'Oculto'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3 bg-violet-500/10 border border-violet-500/20 rounded-xl px-4 py-3">
        <i className="ri-information-line text-violet-400 text-base shrink-0 mt-0.5" />
        <div className="text-[12px] text-violet-300 space-y-1">
          <p><strong>Importante:</strong> Os PDFs gerados pelo PandaPro são documentos reais, <strong>não capturas de tela</strong>.</p>
          <p>Cada exportação gera um arquivo .pdf estruturado com paginação automática, cabeçalhos repetidos em tabelas longas e layout otimizado para impressão.</p>
          <p>As configurações desta aba são aplicadas imediatamente — não é necessário recarregar.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[24px] font-bold text-white">Configurações do Sistema</h1>
          <p className="text-white/40 text-[14px] mt-1">Centro de controle global da plataforma</p>
        </div>
        {isMasterAdmin && (
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-[14px] font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
            {saving ? <><i className="ri-loader-4-line animate-spin" />Salvando...</>
              : saved ? <><i className="ri-check-line" />Salvo!</>
              : <><i className="ri-save-line" />Salvar alterações</>}
          </button>
        )}
      </div>

      {!isMasterAdmin && (
        <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-6">
          <i className="ri-lock-line text-amber-400 text-lg" />
          <p className="text-[13px] text-amber-300">Acesso somente leitura. Apenas <strong>Master Admin</strong> pode alterar configurações.</p>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-6">
          <i className="ri-error-warning-line text-red-400 text-lg" />
          <p className="text-[13px] text-red-300">{error}</p>
        </div>
      )}

      <div className="flex gap-6 min-h-[600px]">
        <div className="w-[210px] shrink-0">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-2 space-y-0.5">
            {TABS.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all cursor-pointer whitespace-nowrap text-left ${
                  activeTab === tab.id
                    ? 'bg-violet-600/20 text-violet-300 border border-violet-500/30'
                    : 'text-white/50 hover:bg-white/5 hover:text-white border border-transparent'
                }`}>
                <i className={`${tab.icon} text-[16px]`} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl p-6 overflow-y-auto">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminConfiguracoesPage;
